import { FormEvent, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  BsDiagram3,
  BsEthernet,
  BsGeoAlt,
  BsPencilSquare,
  BsPlusCircle,
  BsSave,
  BsTrash3,
} from "react-icons/bs";

import InventarioService from "../../../Services/Catalogos/InventarioService";
import { DTOIPInventario } from "../../../DTO/Inventario/DTOIPInventario";
import { DTOSegmentoRed } from "../../../DTO/Inventario/DTOCatalogosInventario";
import {
  DTOGestionarIP,
  DTOGestionarSegmentoRed,
} from "../../../DTO/Inventario/DTOGestionInventario";

interface MensajeInventario {
  encabezado: string;
  mensaje: string;
  color?: string;
}

interface RedEquipoProps {
  idEquipo: number;
  ips: DTOIPInventario[];
  segmentos: DTOSegmentoRed[];
  onRecargar: () => Promise<void>;
  onMensaje: (mensaje: MensajeInventario) => void;
}

type TipoConfirmacion =
  | { tipo: "IP"; id: number; descripcion: string }
  | { tipo: "SEGMENTO"; id: number; descripcion: string }
  | null;

const crearFormularioIP = (): DTOGestionarIP => ({
  accion: "INSERTAR",
  idEquipo: null,
  idSegmento: null,
  direccionIP: "",
  esPrincipal: false,
});

const crearFormularioSegmento = (): DTOGestionarSegmentoRed => ({
  accion: "INSERTAR",
  nombreSegmento: "",
  tipoRed: "",
  segmento: "",
  gateway: "",
});

const obtenerMensajeError = (error: unknown): string => {
  const errorAxios = error as {
    response?: { data?: { mensaje?: string } };
    message?: string;
  };

  return (
    errorAxios.response?.data?.mensaje ??
    errorAxios.message ??
    "Ocurrió un error inesperado."
  );
};

const RedEquipo = ({
  idEquipo,
  ips,
  segmentos,
  onRecargar,
  onMensaje,
}: RedEquipoProps) => {
  const [procesando, setProcesando] = useState(false);
  const [showIP, setShowIP] = useState(false);
  const [ipEditar, setIPEditar] = useState<DTOIPInventario | null>(null);
  const [ipForm, setIPForm] = useState<DTOGestionarIP>(crearFormularioIP);

  const [showSegmentos, setShowSegmentos] = useState(false);
  const [segmentoEditar, setSegmentoEditar] =
    useState<DTOSegmentoRed | null>(null);
  const [segmentoForm, setSegmentoForm] =
    useState<DTOGestionarSegmentoRed>(crearFormularioSegmento);
  const [confirmacion, setConfirmacion] = useState<TipoConfirmacion>(null);

  const segmentoSeleccionado = useMemo(
    () =>
      segmentos.find(
        (segmento) => segmento.idSegmento === ipForm.idSegmento
      ),
    [segmentos, ipForm.idSegmento]
  );

  const abrirNuevaIP = () => {
    setIPEditar(null);
    setIPForm({
      accion: "INSERTAR",
      idEquipo,
      idSegmento: null,
      direccionIP: "",
      esPrincipal: false,
    });
    setShowIP(true);
  };

  const abrirEditarIP = (ip: DTOIPInventario) => {
    setIPEditar(ip);
    setIPForm({
      accion: "ACTUALIZAR",
      idIP: ip.idIP,
      idEquipo: ip.idEquipo,
      idSegmento: ip.idSegmento,
      direccionIP: ip.direccionIP,
      esPrincipal: ip.esPrincipal,
    });
    setShowIP(true);
  };

  const guardarIP = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!ipForm.idSegmento || !ipForm.direccionIP?.trim()) return;

    setProcesando(true);

    try {
      const respuesta = await InventarioService.gestionarIP({
        ...ipForm,
        idEquipo,
        direccionIP: ipForm.direccionIP.trim(),
      });

      setShowIP(false);
      setIPEditar(null);
      onMensaje({
        encabezado: "Éxito",
        mensaje: respuesta.data.mensaje,
        color: "success",
      });
      await onRecargar();
    } catch (errorPeticion) {
      onMensaje({
        encabezado: "Error",
        mensaje: obtenerMensajeError(errorPeticion),
        color: "danger",
      });
    } finally {
      setProcesando(false);
    }
  };

  const abrirNuevoSegmento = () => {
    setSegmentoEditar(null);
    setSegmentoForm(crearFormularioSegmento());
  };

  const abrirEditarSegmento = (segmento: DTOSegmentoRed) => {
    setSegmentoEditar(segmento);
    setSegmentoForm({
      accion: "ACTUALIZAR",
      idSegmento: segmento.idSegmento,
      nombreSegmento: segmento.nombreSegmento,
      tipoRed: segmento.tipoRed,
      segmento: segmento.segmento,
      gateway: segmento.gateway ?? "",
    });
  };

  const guardarSegmento = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !segmentoForm.nombreSegmento?.trim() ||
      !segmentoForm.tipoRed?.trim() ||
      !segmentoForm.segmento?.trim()
    ) {
      return;
    }

    setProcesando(true);

    try {
      const respuesta = await InventarioService.gestionarSegmentoRed({
        ...segmentoForm,
        nombreSegmento: segmentoForm.nombreSegmento.trim(),
        tipoRed: segmentoForm.tipoRed.trim(),
        segmento: segmentoForm.segmento.trim(),
        gateway: segmentoForm.gateway?.trim() || null,
      });

      setSegmentoEditar(null);
      onMensaje({
        encabezado: "Éxito",
        mensaje: respuesta.data.mensaje,
        color: "success",
      });
      await onRecargar();
    } catch (errorPeticion) {
      onMensaje({
        encabezado: "Error",
        mensaje: obtenerMensajeError(errorPeticion),
        color: "danger",
      });
    } finally {
      setProcesando(false);
    }
  };

  const ejecutarEliminacion = async () => {
    if (!confirmacion) return;

    setProcesando(true);

    try {
      const respuesta =
        confirmacion.tipo === "IP"
          ? await InventarioService.gestionarIP({
              accion: "ELIMINAR",
              idIP: confirmacion.id,
              esPrincipal: false,
            })
          : await InventarioService.gestionarSegmentoRed({
              accion: "ELIMINAR",
              idSegmento: confirmacion.id,
            });

      onMensaje({
        encabezado: "Éxito",
        mensaje: respuesta.data.mensaje,
        color: "success",
      });
      setConfirmacion(null);
      await onRecargar();
    } catch (errorPeticion) {
      onMensaje({
        encabezado: "Error",
        mensaje: obtenerMensajeError(errorPeticion),
        color: "danger",
      });
    } finally {
      setProcesando(false);
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white border-0 pt-3 d-flex align-items-center justify-content-between">
          <span className="fw-bold">
            <BsEthernet className="me-2 text-primary" />
            Direcciones IP
          </span>

          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                abrirNuevoSegmento();
                setShowSegmentos(true);
              }}
            >
              <BsDiagram3 className="me-1" />
              Segmentos
            </Button>
            <Button size="sm" variant="outline-primary" onClick={abrirNuevaIP}>
              <BsPlusCircle className="me-1" />
              Agregar IP
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th className="small">Dirección</th>
                <th className="small">Segmento</th>
                <th className="small">Tipo</th>
                <th className="small">Rango</th>
                <th className="small">Gateway</th>
                <th className="small">Principal</th>
                <th className="small">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {ips.map((ip) => (
                <tr key={ip.idIP}>
                  <td className="font-monospace small fw-semibold">
                    {ip.direccionIP}
                  </td>
                  <td className="small">
                    <div className="d-flex align-items-center gap-1">
                      <BsGeoAlt className="text-muted" />
                      {ip.nombreSegmento ?? "—"}
                    </div>
                  </td>
                  <td>
                    <Badge bg="light" text="dark" className="border">
                      {ip.tipoRed ?? "—"}
                    </Badge>
                  </td>
                  <td className="font-monospace small">{ip.segmento ?? "—"}</td>
                  <td className="font-monospace small">{ip.gateway ?? "—"}</td>
                  <td>
                    {ip.esPrincipal ? (
                      <Badge bg="primary">Principal</Badge>
                    ) : (
                      <Badge bg="light" text="secondary" className="border">
                        Secundaria
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => abrirEditarIP(ip)}
                      >
                        <BsPencilSquare />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() =>
                          setConfirmacion({
                            tipo: "IP",
                            id: ip.idIP,
                            descripcion: ip.direccionIP,
                          })
                        }
                      >
                        <BsTrash3 />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {ips.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    Sin direcciones IP asignadas.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={showIP}
        onHide={() => {
          if (!procesando) {
            setShowIP(false);
            setIPEditar(null);
          }
        }}
        centered
        backdrop="static"
      >
        <Form onSubmit={guardarIP}>
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-3 fs-5">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 42,
                  height: 42,
                  color: "#0d6efd",
                  backgroundColor: "#e8f1ff",
                }}
              >
                <BsEthernet size={22} />
              </div>
              <div>
                <div className="fw-bold">
                  {ipEditar ? "Editar dirección IP" : "Asignar dirección IP"}
                </div>
                <div className="text-muted fw-normal" style={{ fontSize: 12 }}>
                  Configuración de red del equipo
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ backgroundColor: "#f8f9fa" }}>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="small fw-semibold mb-0">
                      Segmento de red <span className="text-danger">*</span>
                    </Form.Label>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={() => {
                        setShowIP(false);
                        abrirNuevoSegmento();
                        setShowSegmentos(true);
                      }}
                    >
                      Administrar segmentos
                    </Button>
                  </div>

                  <Form.Select
                    value={ipForm.idSegmento ?? ""}
                    onChange={(event) =>
                      setIPForm({
                        ...ipForm,
                        idSegmento: Number(event.target.value) || null,
                      })
                    }
                    required
                  >
                    <option value="">Seleccione un segmento</option>
                    {segmentos.map((segmento) => (
                      <option
                        key={segmento.idSegmento}
                        value={segmento.idSegmento}
                      >
                        {segmento.nombreSegmento} - {segmento.segmento}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {segmentoSeleccionado && (
                <Col xs={12}>
                  <Alert variant="primary" className="mb-0">
                    <div className="fw-bold mb-2">Información del segmento</div>
                    <Row className="g-2">
                      <Col sm={4}>
                        <div className="small text-muted">Tipo de red</div>
                        <Badge bg="primary">{segmentoSeleccionado.tipoRed}</Badge>
                      </Col>
                      <Col sm={4}>
                        <div className="small text-muted">Rango</div>
                        <div className="font-monospace fw-semibold">
                          {segmentoSeleccionado.segmento}
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className="small text-muted">Gateway</div>
                        <div className="font-monospace fw-semibold">
                          {segmentoSeleccionado.gateway ?? "Sin gateway"}
                        </div>
                      </Col>
                    </Row>
                  </Alert>
                </Col>
              )}

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Dirección IP <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    className="font-monospace"
                    value={ipForm.direccionIP ?? ""}
                    onChange={(event) =>
                      setIPForm({ ...ipForm, direccionIP: event.target.value })
                    }
                    placeholder="Ej. 192.168.1.50"
                    maxLength={50}
                    required
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Check
                  type="switch"
                  id="ip-principal"
                  label="Establecer como IP principal del equipo"
                  checked={ipForm.esPrincipal}
                  onChange={(event) =>
                    setIPForm({ ...ipForm, esPrincipal: event.target.checked })
                  }
                />
                <Form.Text className="text-muted">
                  Las demás IPs del equipo dejarán de marcarse como principales.
                </Form.Text>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="light"
              disabled={procesando}
              onClick={() => {
                setShowIP(false);
                setIPEditar(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={procesando}>
              {procesando ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <BsSave className="me-2" />
                  Guardar IP
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showSegmentos}
        onHide={() => {
          if (!procesando) {
            setShowSegmentos(false);
            setSegmentoEditar(null);
            if (!ipEditar) setShowIP(true);
          }
        }}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-3 fs-5">
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 42,
                height: 42,
                color: "#0d6efd",
                backgroundColor: "#e8f1ff",
              }}
            >
              <BsDiagram3 size={22} />
            </div>
            <div>
              <div className="fw-bold">Segmentos de red</div>
              <div className="text-muted fw-normal" style={{ fontSize: 12 }}>
                Crear, editar o desactivar segmentos
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: "#f8f9fa" }}>
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <Form onSubmit={guardarSegmento}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold">
                        Nombre del segmento *
                      </Form.Label>
                      <Form.Control
                        value={segmentoForm.nombreSegmento ?? ""}
                        onChange={(event) =>
                          setSegmentoForm({
                            ...segmentoForm,
                            nombreSegmento: event.target.value,
                          })
                        }
                        placeholder="Ej. LAN Nuevo Laredo"
                        maxLength={100}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold">
                        Tipo de red *
                      </Form.Label>
                      <Form.Select
                        value={segmentoForm.tipoRed ?? ""}
                        onChange={(event) =>
                          setSegmentoForm({
                            ...segmentoForm,
                            tipoRed: event.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Seleccione el tipo</option>
                        <option value="Privada">Privada</option>
                        <option value="Pública">Pública</option>
                        <option value="WiFi">WiFi</option>
                        <option value="DMZ">DMZ</option>
                        <option value="VPN">VPN</option>
                        <option value="Otro">Otro</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold">
                        Segmento / rango *
                      </Form.Label>
                      <Form.Control
                        className="font-monospace"
                        value={segmentoForm.segmento ?? ""}
                        onChange={(event) =>
                          setSegmentoForm({
                            ...segmentoForm,
                            segmento: event.target.value,
                          })
                        }
                        placeholder="192.168.1.0/24"
                        maxLength={50}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold">Gateway</Form.Label>
                      <Form.Control
                        className="font-monospace"
                        value={segmentoForm.gateway ?? ""}
                        onChange={(event) =>
                          setSegmentoForm({
                            ...segmentoForm,
                            gateway: event.target.value,
                          })
                        }
                        placeholder="192.168.1.1"
                        maxLength={50}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <div className="d-flex justify-content-end gap-2">
                      {segmentoEditar && (
                        <Button
                          type="button"
                          variant="light"
                          onClick={abrirNuevoSegmento}
                        >
                          Cancelar edición
                        </Button>
                      )}
                      <Button type="submit" disabled={procesando}>
                        <BsSave className="me-2" />
                        {segmentoEditar
                          ? "Actualizar segmento"
                          : "Agregar segmento"}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Rango</th>
                    <th>Gateway</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {segmentos.map((segmento) => (
                    <tr key={segmento.idSegmento}>
                      <td>{segmento.nombreSegmento}</td>
                      <td>
                        <Badge bg="light" text="dark" className="border">
                          {segmento.tipoRed}
                        </Badge>
                      </td>
                      <td className="font-monospace small">
                        {segmento.segmento}
                      </td>
                      <td className="font-monospace small">
                        {segmento.gateway ?? "—"}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => abrirEditarSegmento(segmento)}
                          >
                            <BsPencilSquare />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              setConfirmacion({
                                tipo: "SEGMENTO",
                                id: segmento.idSegmento,
                                descripcion: segmento.nombreSegmento,
                              })
                            }
                          >
                            <BsTrash3 />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {segmentos.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No hay segmentos activos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowSegmentos(false);
              setSegmentoEditar(null);
              setShowIP(true);
            }}
          >
            Cerrar y regresar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmacion !== null}
        onHide={() => {
          if (!procesando) setConfirmacion(null);
        }}
        centered
      >
        <Modal.Header
          closeButton
          className="bg-danger text-white"
          closeVariant="white"
        >
          <Modal.Title className="fs-6 fw-bold">Confirmar baja</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          <div className="text-center py-3">
            <BsTrash3 size={36} className="text-danger mb-3" />
            <h5 className="fw-bold">¿Confirmas esta acción?</h5>
            <p className="mb-1 text-muted">Se dará de baja:</p>
            <p className="fw-bold">{confirmacion?.descripcion}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={procesando}
            onClick={() => setConfirmacion(null)}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={procesando}
            onClick={() => void ejecutarEliminacion()}
          >
            {procesando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Procesando...
              </>
            ) : (
              <>
                <BsTrash3 className="me-2" />
                Sí, dar de baja
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RedEquipo;
